import React from 'react';
import theme from '@/style';
import LazyImage from '@/components/basic/image';
import {BasicObject} from '@/types';
import {formatDate} from '@/utils';
const calendar = require('@assets/icons/calendar.webp');
const triangle = require('@assets/icons/triangle.webp');

const BetsTab = (props: {
  active: number;
  changeActive: (i: number) => void;
  currentDate?: Date;
  onSelectDate?: (date?: Date) => void;
  menu: BasicObject[];
}) => {
  const {
    active = 0,
    menu = [],
    changeActive,
    currentDate,
    onSelectDate,
  } = props;
  const [webDateOpen, setWebDateOpen] = React.useState(false);
  const [webYear, setWebYear] = React.useState(
    (currentDate || new Date()).getFullYear(),
  );
  const monthNames = React.useMemo(
    () => [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    [],
  );

  return (
    <div style={stylesWeb.wrap}>
      <div style={stylesWeb.tabs}>
        {menu.map((item, index) => {
          const isSelected = active === index;
          return (
            <button
              key={index}
              onClick={() => changeActive(index)}
              style={stylesWeb.tabButton}
              type="button">
              <span
                style={{
                  ...stylesWeb.tabText,
                  ...(isSelected
                    ? stylesWeb.tabTextActive
                    : stylesWeb.tabTextInactive),
                }}>
                {item.title}
              </span>
              {isSelected ? <div style={stylesWeb.indicator} /> : null}
            </button>
          );
        })}
      </div>
      <div style={stylesWeb.dateWrap}>
        <button
          onClick={() => {
            setWebYear((currentDate || new Date()).getFullYear());
            setWebDateOpen(v => !v);
          }}
          style={stylesWeb.date}
          type="button">
          <LazyImage
            imageUrl={calendar}
            width={16}
            height={16}
            occupancy="transparent"
          />
          <span style={stylesWeb.dateText}>
            {currentDate ? formatDate(currentDate, 'MM/yy') : 'Filter'}
          </span>
          <LazyImage
            imageUrl={triangle}
            width={14}
            height={14}
            occupancy="transparent"
          />
        </button>
        {webDateOpen ? (
          <div style={stylesWeb.datePanel}>
            <div style={stylesWeb.dateArrow} />
            <div style={stylesWeb.yearRow}>
              <button
                type="button"
                style={stylesWeb.yearBtn}
                onClick={() => setWebYear(y => y - 1)}>
                ‹
              </button>
              <span style={stylesWeb.yearText}>{webYear}</span>
              <button
                type="button"
                style={stylesWeb.yearBtn}
                onClick={() => setWebYear(y => y + 1)}>
                ›
              </button>
            </div>
            <div style={stylesWeb.monthGrid}>
              {monthNames.map((month, index) => {
                const selected =
                  currentDate?.getFullYear() === webYear &&
                  currentDate?.getMonth() === index;
                return (
                  <button
                    key={month}
                    type="button"
                    style={{
                      ...stylesWeb.monthBtn,
                      ...(selected ? stylesWeb.monthSelected : null),
                    }}
                    onClick={() => {
                      onSelectDate?.(new Date(webYear, index, 1));
                      setWebDateOpen(false);
                    }}>
                    {month}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BetsTab;

const stylesWeb: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderTop: '1px solid rgb(247,247,247)',
    borderBottom: '1px solid rgb(247,247,247)',
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    padding: '0 0.75rem',
    minHeight: '3rem',
    boxSizing: 'border-box',
    position: 'relative',
  },
  tabButton: {
    position: 'relative',
    height: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    whiteSpace: 'nowrap',
  },
  tabText: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
  },
  tabTextActive: {
    color: theme.basicColor.dark,
    fontWeight: 700,
  },
  tabTextInactive: {
    color: theme.fontColor.second,
    fontWeight: 500,
  },
  indicator: {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    width: '1.25rem',
    height: '0.25rem',
    transform: 'translateX(-50%)',
    backgroundColor: theme.basicColor.primary,
  },
  dateWrap: {
    position: 'relative',
  },
  date: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: '0.5rem',
    marginRight: '0.625rem',
    border: `1px solid ${theme.backgroundColor.grey}`,
    borderRadius: '0.25rem',
    padding: '0.25rem 0.5rem',
    minWidth: '5.4375rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
    gap: '0.25rem',
  },
  dateText: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: theme.basicColor.dark,
    whiteSpace: 'nowrap',
  },
  datePanel: {
    position: 'absolute',
    top: '2.25rem',
    right: '0.625rem',
    zIndex: 20,
    width: '13.625rem',
    backgroundColor: '#fff',
    border: '1px solid #bdbdbd',
    borderRadius: '0.25rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
  },
  dateArrow: {
    position: 'absolute',
    top: '-0.5rem',
    right: '2.75rem',
    width: '0.875rem',
    height: '0.875rem',
    backgroundColor: '#f2f2f2',
    borderLeft: '1px solid #bdbdbd',
    borderTop: '1px solid #bdbdbd',
    transform: 'rotate(45deg)',
  },
  yearRow: {
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0.75rem',
    backgroundColor: '#f2f2f2',
    borderBottom: '1px solid #bdbdbd',
    boxSizing: 'border-box',
    borderTopLeftRadius: '0.25rem',
    borderTopRightRadius: '0.25rem',
  },
  yearBtn: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#cfcfcf',
    cursor: 'pointer',
    fontSize: '2rem',
    lineHeight: '1rem',
    padding: 0,
  },
  yearText: {
    color: theme.basicColor.dark,
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.25rem',
  },
  monthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    rowGap: '0.5rem',
    padding: '0.5rem 1rem 0.75rem',
  },
  monthBtn: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#000',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: '1.5rem',
    padding: 0,
    textAlign: 'center',
  },
  monthSelected: {
    backgroundColor: '#a6c8e8',
    borderRadius: '0.125rem',
  },
};
